import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createShowRealtimeClient,
  type ShowRealtimeClient,
  type ShowRealtimeHandlers,
  type ShowRealtimeSubscription,
} from '@fetmode/api-client';
import type { ShowCaptionEvent, StreamSignalOutgoing } from '@fetmode/types';
import { useAuth } from '@fetmode/app-core';

export interface UseShowRealtimeResult {
  sendStreamSignal: (payload: StreamSignalOutgoing) => void;
  sendShowCaption: (payload: ShowCaptionEvent) => void;
  localPeerId: string | null;
}

export function useShowRealtime(
  apiBaseUrl: string,
  subscription: ShowRealtimeSubscription,
  handlers: ShowRealtimeHandlers,
): UseShowRealtimeResult {
  const { getAccessToken, user } = useAuth();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const clientRef = useRef<ShowRealtimeClient | null>(null);
  const subscriptionRef = useRef(subscription);
  subscriptionRef.current = subscription;
  const [localPeerId, setLocalPeerId] = useState<string | null>(null);

  const sendStreamSignal = useCallback((payload: StreamSignalOutgoing) => {
    clientRef.current?.sendStreamSignal(payload);
  }, []);
  const sendShowCaption = useCallback((payload: ShowCaptionEvent) => {
    clientRef.current?.sendShowCaption(payload);
  }, []);

  useEffect(() => {
    const client = createShowRealtimeClient(
      {
        apiBaseUrl,
        getAccessToken: () => getAccessTokenRef.current(),
      },
      {
        onShowUpdated: (event) => handlersRef.current.onShowUpdated?.(event),
        onShowEnded: (event) => handlersRef.current.onShowEnded?.(event),
        onCamsChanged: (event) => handlersRef.current.onCamsChanged?.(event),
        onShowRequestsChanged: (event) => handlersRef.current.onShowRequestsChanged?.(event),
        onStreamSignal: (event) => handlersRef.current.onStreamSignal?.(event),
        onShowCaption: (event) => handlersRef.current.onShowCaption?.(event),
      },
    );
    clientRef.current = client;
    client.subscribe(subscriptionRef.current);
    const syncPeerId = (): void => {
      setLocalPeerId(client.socket.id ?? null);
    };
    client.socket.on('connect', syncPeerId);
    if (client.socket.connected) {
      syncPeerId();
    }

    return () => {
      client.socket.off('connect', syncPeerId);
      client.disconnect();
      clientRef.current = null;
      setLocalPeerId(null);
    };
  }, [apiBaseUrl, user?.id]);

  useEffect(() => {
    clientRef.current?.subscribe(subscription);
  }, [subscription.username, subscription.showId, subscription.cams]);

  return { sendStreamSignal, sendShowCaption, localPeerId };
}
